// Pure THREE builders that turn a saved model into a lightweight placeable mesh,
// scaled to the model's real size and centered so its footprint sits at the group
// origin with the base at y=0 (floor level), in metres. The async cache +
// fetch/compute orchestration lives in floor/bake-cache.ts.
//
// Two producers, ONE consumer shape:
//  - buildBaked: SYNCHRONOUS — meshes the grid on the calling thread. With
//    includeAO=false it's the fast shell-only fallback (legacy v1 models, or
//    before the off-thread bake lands).
//  - bakedAssetToObject3D: the FAST path — rebuilds the same Object3D from a
//    precomputed BakedAssetCore (geometry buffers) with no meshing, reusing
//    VoxelMesher's exact materials so it's identical to a live mesh.

import * as THREE from "three";
import {
  type BakedAssetCore,
  type CellAABB,
  type SavedModel,
  cellsToGrid,
  gridCellBounds,
  isV2,
  voxelsToGrid,
} from "../model/saved-model";
import { CELL_SIZE, VoxelGrid } from "../model/voxel-grid";
import { makePaletteLookup } from "../ui/palette";
import { type PaletteLookup, VoxelMesher } from "../view/voxel-mesh";
import { type AoArrays, buildAOArrays } from "./ao-mesh";

const IN_TO_M = 0.0254;

// Each bake carries two looks; the lighting rig flips between them by name
// (applyBakeLook, driven from PlacementSystem.setPresentation):
//  - EDIT: the shared VoxelMesher output (greedy-merged faces + edge lines) —
//    the flat editing look.
//  - PRESENTATION: one quad per exposed voxel face with classic 3-neighbor
//    corner AO baked into vertex colors (see ao-mesh.ts).
const BAKE_EDIT = "bake-edit";
const BAKE_PRESENTATION = "bake-presentation";
// Toggle a placement wrapper between the flat editing look and the AO
// presentation look. Operates on the WRAPPER's named children so a shell-only
// bake (no AO mesh yet — before on-demand AO lands) stays VISIBLE in
// presentation rather than vanishing. A no-op on non-wrapper nodes, so it's safe
// to call via traverse().
export function applyBakeLook(wrapper: THREE.Object3D, presentation: boolean): void {
  let edit: THREE.Object3D | undefined;
  let pres: THREE.Object3D | undefined;
  for (const c of wrapper.children) {
    if (c.name === BAKE_EDIT) edit = c;
    else if (c.name === BAKE_PRESENTATION) pres = c;
  }
  if (!edit && !pres) return; // not a bake wrapper
  if (pres) {
    if (edit) edit.visible = !presentation;
    pres.visible = presentation;
  } else if (edit) {
    edit.visible = true; // shell-only: nothing to swap to, keep it shown
  }
  // In the presentation/lighting look, placed models read as lit solids — no
  // black silhouette lines. With a full AO bake the whole edit object hides, so
  // its edges go with it; but the shell-only fallback (AO not yet computed, AO
  // failed, or a legacy model) keeps the edit mesh VISIBLE, which would leave the
  // black edge lines on in lighting mode. So toggle the edit look's edge lines
  // directly. Edge lines are the renderOrder-11 LineSegments (grid lines are 10
  // and stay hidden on placements regardless) — see VoxelMesher.createChunkMesh.
  if (edit) {
    edit.traverse((o) => {
      const line = o as THREE.LineSegments;
      if (line.isLineSegments && line.renderOrder === 11) {
        line.visible = !presentation;
      }
    });
  }
}

// One shared AO material; colors live in the vertices.
const aoMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85 });

// ── Shared transform (the ONLY copy of the placement scale/centering math) ──

function placementScale(saved: SavedModel): { sx: number; sy: number; sz: number } {
  // Per-axis real inches/cell so the placement matches the EXACT canvas
  // dimensions (exact size ÷ canvas cell count), not the voxel-rounded one.
  const cells = {
    x: Math.max(1, Math.round((saved.bounds.maxX - saved.bounds.minX) / CELL_SIZE)),
    y: Math.max(1, Math.round(saved.bounds.height / CELL_SIZE)),
    z: Math.max(1, Math.round((saved.bounds.maxZ - saved.bounds.minZ) / CELL_SIZE)),
  };
  const k = IN_TO_M / CELL_SIZE; // mesher builds at CELL_SIZE m/cell
  return {
    sx: (saved.realIn.w / cells.x) * k,
    sy: (saved.realIn.h / cells.y) * k,
    sz: (saved.realIn.d / cells.z) * k,
  };
}

// Apply per-axis scale + footprint centering (X/Z centered, base resting on
// y=0) to the inner object, from the model's real size and its voxel bounds.
// Empty grid → identity scale, no offset. Shared by buildBaked and the asset
// deserialize so the two are pixel-identical.
function applyPlacementTransform(
  inner: THREE.Object3D,
  saved: SavedModel,
  b: CellAABB | null,
): void {
  if (!b) {
    inner.scale.set(1, 1, 1);
    return;
  }
  const { sx, sy, sz } = placementScale(saved);
  inner.scale.set(sx, sy, sz);
  inner.position.set(
    -((b.minX + b.maxX + 1) / 2) * CELL_SIZE * sx,
    -b.minY * CELL_SIZE * sy,
    -((b.minZ + b.maxZ + 1) / 2) * CELL_SIZE * sz,
  );
}

function f32(a: ArrayLike<number>): THREE.BufferAttribute {
  return a instanceof Float32Array
    ? new THREE.BufferAttribute(a, 3)
    : new THREE.Float32BufferAttribute(a as number[], 3);
}

// Build the AO mesh, applying the model's palette to the per-vertex
// (paletteIndex, shade) the AO pass produced: color = palette.color(pal)*shade.
// Shared by buildBaked (pal/shade from buildAOArrays) and the asset deserialize
// (pal/shade from the stored buffers) so both yield identical vertex colors.
function aoMeshFrom(
  pos: ArrayLike<number>,
  norm: ArrayLike<number>,
  pal: ArrayLike<number>,
  shade: ArrayLike<number>,
  palette: PaletteLookup,
): THREE.Mesh {
  const n = pal.length;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const c = palette.color(pal[i]);
    const s = shade[i];
    col[i * 3] = c.r * s;
    col[i * 3 + 1] = c.g * s;
    col[i * 3 + 2] = c.b * s;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", f32(pos));
  geom.setAttribute("normal", f32(norm));
  geom.setAttribute("color", new THREE.BufferAttribute(col, 3));
  const mesh = new THREE.Mesh(geom, aoMat);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// ── Synchronous fallback: mesh the grid on this thread ──────────────────────

export function buildBaked(
  saved: SavedModel,
  fallback: PaletteLookup,
  includeAO = true,
): THREE.Object3D {
  const grid = new VoxelGrid();
  if (isV2(saved)) voxelsToGrid(saved.voxels, grid);
  else cellsToGrid(saved.cells, grid);
  // voxelsToGrid's setRawChunk bypasses dirty-tracking, so the mesher (which
  // meshes the dirty set) would build nothing — dirty every chunk first.
  grid.markAllChunksDirty();

  // Each placement keeps the colors the model was SAVED with — independent of
  // whatever document is open. Legacy models (no saved palette) fall back to the
  // caller's palette to preserve prior behaviour.
  const palette: PaletteLookup = saved.paletteColors
    ? makePaletteLookup(saved.paletteColors)
    : fallback;

  const mesher = new VoxelMesher(palette);
  mesher.setGridVisible(false);
  mesher.setEdgesVisible(true);
  mesher.sync(grid);

  const inner = mesher.object3D;
  inner.name = BAKE_EDIT;
  applyPlacementTransform(inner, saved, gridCellBounds(grid));

  const wrapper = new THREE.Group();
  wrapper.add(inner);

  const ao: AoArrays | null = includeAO ? buildAOArrays(grid) : null;
  if (ao) {
    const aoMesh = aoMeshFrom(ao.pos, ao.norm, ao.pal, ao.shade, palette);
    aoMesh.name = BAKE_PRESENTATION;
    aoMesh.visible = false; // editing look by default; the rig flips it
    aoMesh.scale.copy(inner.scale);
    aoMesh.position.copy(inner.position);
    wrapper.add(aoMesh);
  }
  return wrapper;
}

// ── Fast path: rebuild the Object3D from a precomputed asset (no meshing) ────

export function bakedAssetToObject3D(
  baked: BakedAssetCore,
  saved: SavedModel,
  fallback: PaletteLookup,
): THREE.Object3D {
  const palette: PaletteLookup = saved.paletteColors
    ? makePaletteLookup(saved.paletteColors)
    : fallback;

  // Reuse VoxelMesher to assemble the merged buffers as one synthetic chunk —
  // identical materials/lines/visibility to a live mesh, zero drift.
  const mesher = new VoxelMesher(palette);
  mesher.setGridVisible(false);
  mesher.setEdgesVisible(true);
  mesher.applyBakedGeometry(baked.shell.faces, baked.shell.gridLines, baked.shell.edgeLines);

  const inner = mesher.object3D;
  inner.name = BAKE_EDIT;
  applyPlacementTransform(inner, saved, baked.cellBounds);

  const wrapper = new THREE.Group();
  wrapper.add(inner);

  if (baked.ao) {
    const aoMesh = aoMeshFrom(
      baked.ao.pos,
      baked.ao.norm,
      baked.ao.pal,
      baked.ao.shade,
      palette,
    );
    aoMesh.name = BAKE_PRESENTATION;
    aoMesh.visible = false;
    aoMesh.scale.copy(inner.scale);
    aoMesh.position.copy(inner.position);
    wrapper.add(aoMesh);
  }
  return wrapper;
}
